import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { trackOnboardingResponse } from "../lib/analytics";
import QUESTIONS from "../data/onboardingQuestions";
import { ArrowRight, Check } from "lucide-react";
import LogoMark from "../components/LogoMark";

/**
 * Build 5 — Onboarding quiz.
 *
 * - Full-screen, one question at a time.
 * - No progress bar / no "step 2 of 6". Documentary intro, not a form.
 * - Question definitions live in /data/onboardingQuestions.js so editorial
 *   can iterate without touching this component.
 * - Each answer fires `tcp_onboarding_response`.
 * - At the end we persist via PUT /api/me/preferences and exit straight
 *   into a starting investigation (the first published topic that overlaps
 *   the chosen interests) — quiz becomes the first pathway, not a setup page.
 */
export default function OnboardingQuizPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Gate the route — anyone (member or not) can take it, but anonymous
    // users get sent to login first. We don't want to lose their answers.
    useEffect(() => {
        const token = (() => {
            try { return localStorage.getItem("ctx_token"); } catch { return null; }
        })();
        if (!token) navigate("/account/login?next=/welcome");
    }, [navigate]);

    const q = QUESTIONS[step];
    const isLast = step === QUESTIONS.length - 1;

    const currentValue = answers[q.id];
    const canProceed = useMemo(() => {
        if (q.kind === "multi") {
            const arr = currentValue || [];
            return arr.length >= (q.min || 1);
        }
        return currentValue !== undefined && currentValue !== null;
    }, [q, currentValue]);

    const recordChoice = (option) => {
        if (q.kind === "multi") {
            const existing = new Set(answers[q.id] || []);
            if (existing.has(option.value)) {
                existing.delete(option.value);
            } else if (existing.size < (q.max || 99)) {
                existing.add(option.value);
            }
            setAnswers({ ...answers, [q.id]: Array.from(existing) });
        } else {
            setAnswers({ ...answers, [q.id]: option.value, [`${q.id}__aux`]: option.aux_value || null });
            // Auto-advance on single-choice, after a beat so the tick reads.
            window.setTimeout(() => advance(), 280);
        }
        // Fire-and-forget event (one per click, including unselects in multi).
        try {
            trackOnboardingResponse({
                q_id: q.id,
                value: option.value,
                aux: option.aux_value || null,
                kind: q.kind,
            });
        } catch (_) { /* swallow */ }
    };

    const advance = async () => {
        if (!isLast) {
            setStep((s) => s + 1);
            return;
        }
        await submit();
    };

    const submit = async () => {
        setSubmitting(true);
        setError(null);

        // Translate quiz answers into the preferences PUT payload.
        const payload = { onboarding_responses: { ...answers }, mark_onboarding_complete: true };
        for (const def of QUESTIONS) {
            if (!def.writes_to_preference) continue;
            const v = answers[def.id];
            if (v === undefined || v === null) continue;
            payload[def.writes_to_preference] = v;
        }

        try {
            await api.put("/me/preferences", payload);
        } catch (e) {
            // Save failure is recoverable — surface but don't block the
            // user from continuing into the rabbit-hole. The dashboard
            // will read whatever DID persist next time.
            setError("We couldn't save everything, but you can still explore. We'll keep trying.");
        }

        // Exit into a starting investigation — pick one whose tags overlap
        // chosen interests. This makes the quiz BECOME the first pathway.
        let nextSlug = null;
        try {
            const interests = answers.interests || [];
            const res = await api.get("/topics?status=published&limit=80");
            const topics = Array.isArray(res.data) ? res.data : (res.data?.items || []);
            const interestSet = new Set(interests);
            const scored = topics.map((t) => {
                const tags = new Set((t.tags || []).map((s) => String(s).toLowerCase()));
                const cat = String(t.category || "").toLowerCase();
                let overlap = 0;
                for (const i of interestSet) {
                    if (tags.has(i) || cat.includes(i.replace(/-/g, " "))) overlap += 1;
                }
                return { slug: t.slug, overlap, priority: t.priority || 0 };
            }).sort((a, b) => b.overlap - a.overlap || b.priority - a.priority);
            if (scored[0]?.slug) nextSlug = scored[0].slug;
        } catch (_) { /* fall through */ }

        setSubmitting(false);
        if (nextSlug) navigate(`/topic/${nextSlug}`);
        else navigate("/dashboard");
    };

    if (!q) return null;

    return (
        <div className="min-h-screen bg-[#0A0F1A] text-white selection:bg-[#56CCF2]/30">
            <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
                <div className="mb-12">
                    <LogoMark size="sm" dataTestId="onboarding-logo" />
                </div>
                <div
                    data-testid={`quiz-step-${q.id}`}
                    className="animate-[fadeIn_400ms_ease-out]"
                    style={{ animation: "fadeIn 400ms ease-out" }}
                >
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#56CCF2] font-medium mb-6">
                        {q.eyebrow}
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-4">
                        {q.title}
                    </h1>
                    {q.helper ? (
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed mb-10 max-w-xl">
                            {q.helper}
                        </p>
                    ) : null}

                    <div className={`grid ${q.options.length > 6 ? "sm:grid-cols-2" : "sm:grid-cols-1"} gap-3 mb-10`}>
                        {q.options.map((opt, idx) => {
                            const isSelected = q.kind === "multi"
                                ? (answers[q.id] || []).includes(opt.value)
                                : answers[q.id] === opt.value && (answers[`${q.id}__aux`] || null) === (opt.aux_value || null);
                            return (
                                <button
                                    key={`${opt.value}-${idx}`}
                                    type="button"
                                    data-testid={`quiz-option-${q.id}-${opt.value}${opt.aux_value ? "-" + opt.aux_value : ""}`}
                                    onClick={() => recordChoice(opt)}
                                    className={
                                        "group w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center justify-between " +
                                        (isSelected
                                            ? "bg-[#111827] border-[#56CCF2] text-white"
                                            : "bg-[#0B1220] border-[#243044] text-[#CBD5E1] hover:border-[#2F80ED] hover:bg-[#111827]")
                                    }
                                >
                                    <span className="text-[15px] leading-relaxed">{opt.label}</span>
                                    {isSelected ? (
                                        <Check className="w-4 h-4 text-[#56CCF2] flex-shrink-0 ml-3" />
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>

                    {q.kind === "multi" ? (
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-xs text-[#64748B]">
                                {(answers[q.id] || []).length} selected
                                {q.max ? ` · up to ${q.max}` : ""}
                            </span>
                            <button
                                data-testid="quiz-continue"
                                type="button"
                                disabled={!canProceed || submitting}
                                onClick={advance}
                                className={
                                    "inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all " +
                                    (canProceed && !submitting
                                        ? "bg-[#56CCF2] text-[#0A0F1A] hover:bg-[#7BD7F5]"
                                        : "bg-[#243044] text-[#64748B] cursor-not-allowed")
                                }
                            >
                                {isLast ? (submitting ? "Opening your first thread…" : "Begin investigating") : "Continue"}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    ) : null}

                    {error ? (
                        <p className="mt-6 text-xs text-[#94A3B8]">{error}</p>
                    ) : null}
                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
